import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Renderer2,
  inject,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { ComponentAccess } from '../../../../common/decorators/component-access.decorator';
import { ThemeService } from '../../service/theme.service';

@Component({
  selector: 'app-coming-soon',
  standalone: true,
  imports: [],
  templateUrl: './coming-soon.component.html',
  styleUrls: ['./coming-soon.component.scss'],
})
@ComponentAccess('ComingSoonComponent')
export class ComingSoonComponent implements AfterViewInit {
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);
  private platform = inject(Platform);
  readonly themeService = inject(ThemeService);
  /** The pending animation frame. The loop re-arms itself forever, so it has to
   *  be cancelled when the screen goes away or it animates nothing for good. */
  private frameId: number | null = null;

  width: number = window.innerWidth;
  height: number = window.innerHeight;
  floaters: any[] = [];
  isIOS: boolean = false;
  isMobile: boolean = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.frameId !== null) cancelAnimationFrame(this.frameId);
      this.frameId = null;
    });
    this.isIOS = this.platform.is('ios');
    this.isMobile = this.platform.is('mobile');
  }

  @HostListener('window:resize')
  onResize() {
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    // Update floater positions when window resizes
    if (this.floaters.length > 0) {
      for (const floater of this.floaters) {
        if (floater.x > this.width) floater.x = Math.random() * this.width;
        if (floater.y > this.height) floater.y = Math.random() * this.height;
      }
    }
  }

  ngAfterViewInit(): void {
    this.initFloaters();
  }

  initFloaters(): void {
    const floatersContainer = this.el.nativeElement.querySelector('#floaters');
    if (!floatersContainer) return;

    // Adjust number of floaters based on screen size
    const numFloaters = this.isMobile ? 14 : 22;

    for (let i = 0; i < numFloaters; i++) {
      const floaterEl = this.renderer.createElement('div');
      this.renderer.addClass(floaterEl, 'floater');

      // Soft glowing orb — size varies for depth (px set inline, styled via .floater)
      const size = Math.round(Math.random() * 26 + 10);
      floaterEl.style.width = `${size}px`;
      floaterEl.style.height = `${size}px`;

      this.renderer.appendChild(floatersContainer, floaterEl);

      // Create more varied angles and speeds
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 1.2 + 0.4;

      // Add varying opacity and size to floaters
      const scale = Math.random() * 0.4 + 0.8;
      const opacity = Math.random() * 0.25 + 0.12;

      this.floaters.push({
        el: floaterEl,
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        scale: scale,
        opacity: opacity,
      });

      // Apply initial scale and opacity
      floaterEl.style.transform = `translate(${this.floaters[i].x}px, ${this.floaters[i].y}px) scale(${scale}) rotate(0deg)`;
      floaterEl.style.opacity = opacity.toString();
    }

    this.animateFloaters();
  }

  animateFloaters(): void {
    const radius = this.isMobile ? 20 : 24;
    const avoidFactor = 0.06;

    // Apply a slight gravity effect toward the center to prevent floaters from being stuck at edges
    const centerPullFactor = 0.0005;
    const centerX = this.width / 2;
    const centerY = this.height / 2;

    for (const floater of this.floaters) {
      // Update position
      floater.x += floater.vx;
      floater.y += floater.vy;

      // Apply subtle center pull
      const dx = centerX - floater.x;
      const dy = centerY - floater.y;
      const dist = Math.hypot(dx, dy);
      if (dist > this.width / 3) {
        floater.vx += (dx / dist) * centerPullFactor;
        floater.vy += (dy / dist) * centerPullFactor;
      }

      // Apply bounds checking with wrap-around
      if (floater.x < -radius) floater.x = this.width + radius;
      if (floater.x > this.width + radius) floater.x = -radius;
      if (floater.y < -radius) floater.y = this.height + radius;
      if (floater.y > this.height + radius) floater.y = -radius;

      // Apply speed limits
      const maxSpeed = 1.8;
      const speedSquared = floater.vx * floater.vx + floater.vy * floater.vy;
      if (speedSquared > maxSpeed * maxSpeed) {
        const ratio = maxSpeed / Math.sqrt(speedSquared);
        floater.vx *= ratio;
        floater.vy *= ratio;
      }
    }

    // Floater collision avoidance
    for (let i = 0; i < this.floaters.length; i++) {
      for (let j = i + 1; j < this.floaters.length; j++) {
        const a = this.floaters[i],
          b = this.floaters[j];
        const dx = b.x - a.x,
          dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);

        if (dist > 0 && dist < radius * 1.8) {
          const repX = (a.x - b.x) / dist;
          const repY = (a.y - b.y) / dist;
          const strength = (avoidFactor * (1.8 * radius - dist)) / dist;

          a.vx += repX * strength;
          a.vy += repY * strength;
          b.vx -= repX * strength;
          b.vy -= repY * strength;
        }
      }
    }

    // Update floater display
    for (const floater of this.floaters) {
      const deg = (Math.atan2(floater.vy, floater.vx) * 180) / Math.PI;
      floater.el.style.transform = `translate(${floater.x}px, ${floater.y}px) scale(${floater.scale}) rotate(${deg}deg)`;
    }

    // Browsers do not run animation frames while hidden, so this pauses itself.
    this.frameId = requestAnimationFrame(() => this.animateFloaters());
  }
}
